import { memo, useCallback, useEffect, useState } from 'react';
import { useObservable } from 'react-use-observable';

import { Button, Grid, Modal } from 'antd';

import type { ContentNew } from 'v2/interfaces/settings';
import analyticsService from 'v2/services/analytics';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { settingsFactory } from 'v2/services/settings';
import { uiManagerService } from 'v2/services/uiManager';

import NoDangerouslyHTML from '../NoDangerouslyHTML';

const { useBreakpoint } = Grid;

const LOCAL_STORAGE_KEY = 'modal_news_seen';

export interface ModalNewsProps {
  disabled?: boolean;
}

export const ModalNews = memo(({ disabled }: ModalNewsProps) => {
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const [loadingTours] = useObservable(() => eduzzAccountsService.getLoadingTours(), []);
  const [currentTour] = useObservable(() => uiManagerService.getCurrentlyTour(), []);
  const [currentStep, setCurrentStep] = useState(1);

  const [news, setNews] = useState<ContentNew[]>([]);
  const [open, setOpen] = useState(false);

  const [contentNews] = useObservable(() => settingsFactory.getNews(), []);
  const [alreadyTrackedSeenNews, setAlreadyTrackedSeenNews] = useState<string[]>([]);
  const [shouldTrackSeenNews, setShouldTrackSeenNews] = useState<ContentNew>();

  const saveNewsToLocalStorage = (news: string[]) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(news));
  };

  const handleNext = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleFinish = () => {
    const updatedStoragedKeys = news.map(modal => modal.id);
    saveNewsToLocalStorage(updatedStoragedKeys);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickCTA = (e: Event, el: Element) => {
    const title = el.attributes.getNamedItem('data-title').value;
    const link = el.attributes.getNamedItem('data-link').value;
    if (!title || !link) return;

    e.preventDefault();

    analyticsService.sendEvent(`${title} - Clicked CTA`, `${title} - Modal News`);

    const target = el.attributes.getNamedItem('data-target').value;
    window.open(link, target);
  };

  useEffect(() => {
    if (!contentNews) return;

    const storagedKeys = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || [];
    const notSeenContents = contentNews.filter(content => !storagedKeys.includes(content.id));
    const shouldShowNews = notSeenContents.length && loadingTours !== null && !loadingTours && !currentTour;

    if (shouldShowNews) {
      setNews(notSeenContents);
      setOpen(true);
    }
  }, [contentNews, currentTour, loadingTours]);

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === news.length;

  const currentNews = news[currentStep - 1];

  useEffect(() => {
    if (!shouldTrackSeenNews) return;

    const alreadyTrackedNews = alreadyTrackedSeenNews.includes(shouldTrackSeenNews.id);

    if (alreadyTrackedNews) return;

    analyticsService.sendEvent(
      `${shouldTrackSeenNews.title} - Modal News Viewed`,
      `${shouldTrackSeenNews.title} - Modal News`
    );
  }, [shouldTrackSeenNews, alreadyTrackedSeenNews]);

  useEffect(() => {
    if (!shouldTrackSeenNews) return;
    setAlreadyTrackedSeenNews(prev => [...prev, shouldTrackSeenNews.id]);
  }, [shouldTrackSeenNews]);

  const elemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node !== null) {
        const cta = window.document.querySelector('#modal-news-cta');
        const title = cta?.attributes.getNamedItem('data-title').value;
        setShouldTrackSeenNews(!!cta ? { ...currentNews, title } : null);
        if (!cta) return;
        cta.addEventListener('click', e => handleClickCTA(e, cta));
      }
    },

    [currentNews]
  );

  if (!currentNews) return null;

  return (
    <Modal
      open={open && !disabled}
      onCancel={handleClose}
      width={isMobile ? '100vw' : '40vw'}
      maskClosable={false}
      title={currentNews.title}
      footer={
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <Button type='text' onClick={handleClose}>
            Ver depois
          </Button>
          <div className='flex flex-wrap items-center gap-2'>
            {!isFirstStep && (
              <Button type='text' onClick={handlePrev}>
                Anterior
              </Button>
            )}
            {!isLastStep && (
              <Button type='primary' onClick={handleNext}>
                Próximo
              </Button>
            )}
            {isLastStep && (
              <Button type='primary' onClick={handleFinish}>
                Entendi
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className='flex flex-col gap-4'>
        {currentNews.imageUrl && (
          <img className='max-w-full' src={currentNews.imageUrl} alt={currentNews.title} loading='lazy' />
        )}
        <div ref={elemRef} className='mb-0 [&_p:not(last-of-type)]:mb-4!' tabIndex={-1}>
          <NoDangerouslyHTML html={currentNews.description} />
        </div>
      </div>
    </Modal>
  );
});
